import { useMutation, useQuery, useQueryClient } from 'react-query';
import axios from 'axios';
import { ExtraPayDoc, ExtraPayApprovePayload } from '../models/extraPay';

const apiUrl = process.env.REACT_APP_BASIX_API || '';

const getDocs = async (): Promise<ExtraPayDoc[]> => {
  const { data } = await axios.get(`${apiUrl}ExtraPayDoc`);
  return data;
};

// item 473 — the Payroll+ extra-pay documents. List feeds both the accountant's by-user view
// and an earner's own history (server self-scopes non-leadership to their own docs). Approve is
// leadership-only (mints the numbered doc); MarkEntered is the accountant's one click; Reopen is
// leadership correction. Each mutation refreshes the list + the commission/timesheet caches that
// feed the approval queue.
const useExtraPay = () => {
  const queryClient = useQueryClient();
  const query = useQuery('extraPayDocs', getDocs);

  const invalidate = () => {
    queryClient.invalidateQueries('extraPayDocs');
    queryClient.invalidateQueries('commissions');
    queryClient.invalidateQueries('timeEntries');
    queryClient.invalidateQueries('payrollCycle');
  };

  const approve = useMutation(
    (payload: ExtraPayApprovePayload) => axios.post(`${apiUrl}ExtraPayDoc/Approve`, payload),
    { onSuccess: invalidate },
  );

  // item 485 — decline a submitted batch: sends the earner's entries back to draft (no doc minted)
  const decline = useMutation(
    (payload: { docType: number; earnerUserId: string; entryIds: string[] }) => axios.post(`${apiUrl}ExtraPayDoc/Decline`, payload),
    { onSuccess: invalidate },
  );

  const markEntered = useMutation(
    (docId: string) => axios.post(`${apiUrl}ExtraPayDoc/MarkEntered`, { docId }),
    { onSuccess: invalidate },
  );

  const reopen = useMutation(
    (docId: string) => axios.post(`${apiUrl}ExtraPayDoc/Reopen`, { docId }),
    { onSuccess: invalidate },
  );

  const createIc = useMutation(
    (payload: {
      earnerUserId: string; amount: number; icInvoiceNumber?: string;
      periodStart: string; periodEnd: string; notes?: string; attachmentFileId?: string;
    }) => axios.post(`${apiUrl}ExtraPayDoc/CreateIc`, payload),
    { onSuccess: invalidate },
  );

  return {
    ...query,
    docs: query.data ?? [],
    approve: approve.mutateAsync,
    decline: decline.mutateAsync,
    markEntered: markEntered.mutateAsync,
    reopen: reopen.mutateAsync,
    createIc: createIc.mutateAsync,
    isApproving: approve.isLoading,
    isDeclining: decline.isLoading,
    isCreatingIc: createIc.isLoading,
  };
};

export default useExtraPay;
